<template>
  <div>
    <HttpContentView :endpoint="utilities.path('players/@', playerId)" v-model:reload="reload">
      <template v-slot:content="{ data: player }">
        <div class="row g-4">
          <div class="col-12 col-md-6 mb-4">
            <h3>{{ translate("actions")}}</h3>
            <div class="card">
              <div class="card-body">
                <div class="row align-items-center g-4">
                  <div v-if="authorization.moduleEnabled(modules.punishments) && player.online && authorization.permission('modules.punishments.kicks.create')" class="col-6">
                    <ButtonKickPlayer :community-id="communityId" :player-id="playerId" @kick="reload = true">
                      <template v-slot:target="{ show }">
                        <ButtonView type="soft" color="info" class="w-100" @click="show">
                          <div class="d-flex align-items-center justify-content-center g-2">
                            <i class="fas fa-running"></i>
                            <span>{{ translate("kick") }}</span>
                          </div>
                        </ButtonView>
                      </template>
                    </ButtonKickPlayer>
                  </div>
                  <div class="col-6" v-if="authorization.moduleEnabled(modules.punishments) && authorization.permission('modules.punishments.bans.create')">
                    <ButtonBanPlayer :community-id="communityId" :player-id="player.id" @ban="reload = true">
                      <template v-slot:target="{ show }">
                        <ButtonView type="soft" color="danger" class="w-100" @click="show">
                          <div class="d-flex align-items-center justify-content-center g-2">
                            <i class="fas fa-ban"></i>
                            <span>{{ translate("ban") }}</span>
                          </div>
                        </ButtonView>
                      </template>
                    </ButtonBanPlayer>
                  </div>
                  <div class="col-6" v-if="authorization.moduleEnabled(modules.punishments) && authorization.permission('modules.punishments.warnings.create')">
                    <CreateWarningButtonView :community-id="communityId" :player-id="player.id" @success="reload = true">
                      <template v-slot:target="{ show }">
                        <ButtonView type="soft" color="warning" class="w-100" @click="show">
                          <div class="d-flex align-items-center justify-content-center g-2">
                            <i class="fas fa-exclamation-circle"></i>
                            <span>{{ translate("warn") }}</span>
                          </div>
                        </ButtonView>
                      </template>
                    </CreateWarningButtonView>
                  </div>
                  <div v-if="authorization.adminRole(adminRoles.superAdmin)" class="col-6">
                    <TargetedModalView class="w-100" size="small">
                      <template v-slot:target="{ show }">
                        <ButtonView type="soft" color="danger" class="w-100" @click="show">
                          <div class="d-flex align-items-center justify-content-center g-2">
                            <i class="fas fa-trash"></i>
                            <span>{{ translate('delete_player') }}</span>
                          </div>
                        </ButtonView>
                      </template>
                      <template v-slot:body="{ hide }">
                        <HttpFormView :endpoint="utilities.path('players/@', playerId)" method="delete" @success="router.push({ name: 'community.players' })">
                          <template v-slot:form="{ submit, loading }">
                            <div class="d-flex align-items-center g-4">
                              <ButtonView type="normal" color="danger" class="w-100" @click="submit">
                                {{ translate("delete_player") }}
                              </ButtonView>
                              <ButtonView type="normal" color="secondary" class="w-100" @click="hide">
                                {{ translate("cancel") }}
                              </ButtonView>
                            </div>
                            <SpinnerView :active="loading" color="danger"/>
                          </template>
                        </HttpFormView>
                      </template>
                    </TargetedModalView>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6 mb-4">
            <h3>{{ translate("identifiers") }}</h3>
            <div class="list-group">
              <PlayerIdentifierView :identifier="player.identifier">
                <template v-slot:target="{ show }">
                    <div class="list-group-item list-group-item-action" @click="show">
                        <div class="d-flex align-items-center justify-content-between g-3">
                            <div class="d-flex align-items-center g-3" style="overflow: hidden">
                                <div class="d-flex align-items-center g-2" style="overflow: hidden">
                                    <i :class="player.identifier.type.icon"></i>
                                    <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">{{ player.identifier.value }}</span>
                                </div>
                                <a v-if="player.identifier.providerProfileUrl" class="d-inline-block text-primary" style="font-size: 0.95em" target="_blank" @click.stop :href="player.identifier.providerProfileUrl">
                                    <i class="fas fa-external-link-alt"></i>
                                </a>
                            </div>
                            <div class="p-2 rounded-2 bg-secondary">{{ translate("primary")}}</div>
                        </div>
                    </div>
                </template>
              </PlayerIdentifierView>
              <PlayerIdentifierView v-for="identifier in authorization.permission('community.players.identifiers.view') ? player.identifiers : []" :key="identifier.id" :identifier="identifier">
                  <template v-slot:target="{ show }">
                      <div class="list-group-item list-group-item-action" @click="show">
                          <div class="d-flex align-items-center justify-content-between g-3">
                              <div class="d-flex align-items-center g-3" style="overflow: hidden; text-overflow: ellipsis">
                                  <div class="d-flex align-items-center g-2" style="overflow: hidden; text-overflow: ellipsis">
                                      <i :class="identifier.type.icon"></i>
                                      <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">{{ identifier.value }}</span>
                                  </div>
                                  <a v-if="identifier.providerProfileUrl" class="btn btn-normal btn-shaded btn-primary" style="font-size: 0.95em" target="_blank" @click.stop :href="identifier.providerProfileUrl">
                                      <i class="fas fa-external-link-alt fa-fw"></i>
                                  </a>
                              </div>
                              <TargetedModalView v-if="authorization.permission('community.players.edit') && !identifier.primary" size="small">
                                  <template v-slot:target="{ show }">
                                      <ButtonView type="shaded" color="warning" class="m-n2" @click.stop="show">
                                          <i class="fas fa-unlink fa-fw"></i>
                                      </ButtonView>
                                  </template>
                                  <template v-slot:body="{ hide }">
                                      <HttpFormView :endpoint="utilities.path('players/@/identifiers/@', playerId, identifier.id)" method="delete" @success="reload = true">
                                          <template v-slot:form="{ submit, loading }">
                                              <div class="d-flex align-items-center g-4">
                                                  <ButtonView type="normal" color="warning" class="w-100" @click="submit">
                                                      {{ translate("unlink") }}
                                                  </ButtonView>
                                                  <ButtonView type="normal" color="secondary" class="w-100" @click="hide">
                                                      {{ translate("cancel") }}
                                                  </ButtonView>
                                              </div>
                                              <SpinnerView :active="loading" color="danger"/>
                                          </template>
                                      </HttpFormView>
                                  </template>
                              </TargetedModalView>
                          </div>
                      </div>
                  </template>
              </PlayerIdentifierView>
            </div>
          </div>
          <div class="col-12 col-md-6 mb-4" v-if="authorization.permission('community.players.notes.view') && (player.notes?.value || authorization.permission('community.players.notes.edit'))">
            <div class="card">
              <div class="card-body">
                <div class="d-flex align-items-center justify-content-between g-4">
                  <h3 class="mb-0 d-flex align-items-center g-2">
                    <i class="fas fa-note text-primary fa-fw"></i>
                    {{ translate("notes") }}
                  </h3>
                  <ButtonView v-if="authorization.permission('community.players.notes.edit')" type="normal" color="primary" size="small" @click="editingNotes = true">
                    <div class="d-flex align-items-center g-2">
                      <i class="fas fa-edit"></i>
                      <span>{{ translate("edit") }}</span>
                    </div>
                  </ButtonView>
                </div>
                <div v-if="player.notes?.value && player.notes.value.length > 0 || player.notes?.updatedAt" class="mt-4">
                  <div v-if="player.notes?.value && player.notes.value.length > 0" class="d-flex justify-content-between align-items-center">
                    <div style="white-space: pre-line; word-break: break-word">{{ player.notes.value }}</div>
                  </div>
                  <div v-if="player.notes?.updatedAt" class="d-flex justify-content-between align-items-center small mb-n2 mt-3">
                    <div class="text-muted">{{ translate("updated_at") }}: {{ utilities.formatDatetime(player.notes.updatedAt) }}</div>
                    <PlayerView v-if="player.notes.updatedBy" :player="player.notes.updatedBy"/>
                  </div>
                </div>
              </div>
            </div>
            <ModalView v-if="authorization.permission('community.players.notes.edit')" v-model:active="editingNotes" size="large">
              <template v-slot:body>
                <HttpFormView :endpoint="utilities.path('players/@/notes', player.id)" method="PUT" :content="player.notes" @success="editingNotes = false; toasts.create({ color: 'success', content: translate('changes_saved')}); reload = true">
                  <template v-slot:form="{ content, errors, submit }">
                    <InputView type="textarea" v-if="authorization.permission('community.players.notes.edit')" :title="translate('value')" :errors="errors.value" :max="512" v-model:value="content.value"/>
                    <div class="d-flex align-items-center g-4 mt-4">
                      <ButtonView type="normal" color="primary" class="w-100" @click="submit">
                        {{ translate("save") }}
                      </ButtonView>
                      <ButtonView type="normal" color="secondary" class="w-100" @click="editingNotes = false">
                        {{ translate("cancel") }}
                      </ButtonView>
                    </div>
                  </template>
                </HttpFormView>
              </template>
            </ModalView>
          </div>
        </div>
      </template>
    </HttpContentView>
  </div>
</template>

<script setup>
import {InputView, HttpFormView, ModalView, HttpContentView, ButtonView, utilities, SpinnerView, TargetedModalView, toasts} from "@/niftybit";
import PlayerView from "@/components/other/PlayerView.vue";
import PlayerIdentifierView from "@/systems/modules/players/identifiers/PlayerIdentifierView.vue";
import authorization from "@/authorization";
import {ref} from "vue";
import ButtonKickPlayer from "@/systems/modules/punishments/kicks/ButtonKickPlayer.vue";
import {adminRoles} from "@/systems/main/adminRoles";
import {useRouter} from "vue-router";
import ButtonBanPlayer from "@/systems/modules/punishments/bans/ButtonBanPlayer.vue";
import {modules} from "@/systems/modules/modules";
import CreateWarningButtonView from "@/systems/modules/punishments/warnings/CreateWarningButtonView.vue";

defineProps({
  communityId: String,
  playerId: String
});

const router = useRouter();

const reload = ref(false);
const editingNotes = ref(false);

</script>
